/*!
 * Bearbottom Fit Finder, recommendation engine (reference implementation v1.2, calibrated 9/23/2026)
 * Pure functions, no DOM. Works in the browser (window.BBFitEngine) and Node (module.exports).
 * All measurements are inches / pounds. Convert metric input before calling.
 *
 * Model summary
 *  1. Estimate the shopper's body: chest (from height + weight + body shape, calibrated to staff fits)
 *     and waist (usual pant waist size + 2", per the Bearbottom size chart).
 *  2. Tops: each product has an "intended ease" = median(garment chest − chart body chest) across sizes.
 *     A size's target body chest = garment chest − intended ease. We pick the size whose target is closest.
 *  3. Bottoms/briefs: anchored to the (normalised) size chart waist. If a style's waistband runs ≥1" smaller or
 *     larger than the catalog norm for its group, its targets shift by that amount (e.g. Stretch Travel Jean runs small).
 *     Boxer briefs use the chart only (negative-ease knit; brief spec sheets use mixed conventions).
 *  4. When the shopper lands between sizes, tie-breakers use fit class + body shape.
 *  5. Inseam: leg geometry from height drives the pant-length pick and the short-length sketch.
 * Coefficients live in CONFIG so they can be tuned after launch without code changes.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.BBFitEngine = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var SIZE_ORDER = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

  var CONFIG = {
    // Bearbottom size chart (body measurements, midpoints of each range)
    chartChestMid: { S: 33.5, M: 36.5, L: 39.5, XL: 42.5, XXL: 45.5, XXXL: 48.5 },
    // Body-waist target per size (usual waist + 2"). Normalised from the size chart so each size spans 3 labels:
    // S ≤29, M 30–32, L 33–35, XL 36–38, XXL 39–41, XXXL 42–44 (the 2021 chart has a gap at 35 and overlap at 42).
    // v1.1 (9/23/2026): calibrated with staff fits, a 33–34 pant size wears M in stretch bottoms.
    // Label bands: S ≤29, M 30–33, L 34–36, XL 37–39, XXL 40–42, XXXL 43+ (body = label + 2).
    // v1.5 (2026-10-19): Fit Finder orders on 9+ bottoms came back too small vs. shoppers sizing themselves, so the
    // v1.1 staff shift is undone from L up (S/M boundary unchanged): 33/36/39 waists sit on the boundary again and take the bigger size
    // unless very lean (BMI < 21). Staff member Josh (w33, wears M) now gets L with M as the alternate.
    chartWaistTarget: { S: 29, M: 34, L: 36, XL: 39, XXL: 42, XXXL: 45 },
    labelToBodyWaist: 2,            // "usual waist 32" ≈ 34" body waist (chart: pants size vs waist rows)
    // v1.1: chest estimate fit to Bearbottom staff (5'9" 135 lb → S, 5'10" 170 lb → M, 5'10.5" 186 lb → L)
    // and anchored to the size chart (S 32–35, M 35–38, L 38–41). Replaces the general-population regression,
    // which ran 2–3" large for lean builds.
    chestRegression: { base: 37.8, weightRef: 170, heightRef: 70, perLb: 0.115, perIn: -0.25 },
    stomachAdj: { flat: 0.5, average: 0, round: -1 },   // weight carried in the stomach → slightly less chest
    chestShapeAdj: { slim: -1.5, average: 0, broad: 1.5 },
    hipFromWaist: { flat: 4, average: 4, round: 2.5 },  // v1.2: flat = average (a flat stomach doesn't mean a bigger seat)
    // v1.3 (2026-09-25): fuller builds carry more seat than their waist implies. Adds hipPerBmi" per BMI point
    // above hipBmiRef, so the seat check sizes them up in slim-seat styles. Customer 6'0" 220 lb w33 got M in
    // the Stretch Chino Pant and needed L. Changes 0.1% of results, all BMI 28+; staff cases unaffected.
    hipBmiRef: 25,
    hipPerBmi: 0.2,
    // Catalog median waistband midpoint ((relaxed+extended)/2) per size, from the 9/2026 spec set.
    // A style whose waistband runs ≥ waistOffsetThreshold" smaller/larger than its group shifts its targets.
    waistbandNorm: {
      pants:  { S: 31.25, M: 34, L: 37, XL: 40, XXL: 43, XXXL: 46 },
      shorts: { S: 31.25, M: 34.25, L: 37.25, XL: 40.25, XXL: 43.25, XXXL: 46.25 }
    },
    waistOffsetThreshold: 1.0,
    bmiLeanSmall: 21,               // bottoms between sizes: leaner builds take the smaller size…
    bmiLeanBig: 28,                 // …fuller builds the bigger one
    betweenBand: [0.45, 0.55],      // bottoms: fraction of the gap between two sizes treated as "between sizes"
    // v1.4 (2026-10-19): bottoms ask about thighs instead of chest + stomach. Athletic thighs in a Modern fit
    // bottom size up once the shopper is this far toward the next size (waists are whole inches, so in practice
    // anyone 1"+ over a size's target). Slim never sizes down. Briefs ignore thighs.
    thighShapes: ['slim', 'average', 'athletic'],
    athleticModernUpFrom: 0.15,
    topBetweenBand: [0.35, 0.65],   // tops: wider, so near-boundary shoppers see the other size as an alternate        // fraction of the gap between two sizes treated as "between sizes"
    minHipEase: 1.5,
    minSweepEase: 2,                // tops: garment sweep circumference must clear body waist by this much
    // Length note only for tall shoppers in a style that runs ≥1" shorter than Bearbottom's median for that type + size.
    tallFrom: 74,                   // 6'2"
    lengthShortBy: 1.0,
    bodyLengthNorm: {
      tee:       { S: 27.5, M: 28.25, L: 29, XL: 30, XXL: 31, XXXL: 31.5 },
      polo:      { S: 26.75, M: 27.75, L: 28.75, XL: 29.75, XXL: 30.5, XXXL: 31.75 },
      tank:      { S: 27, M: 28, L: 29, XL: 30, XXL: 31 },
      button_up: { S: 29, M: 29.5, L: 30.375, XL: 31.125, XXL: 31.875, XXXL: 32.5 },
      knit:      { S: 28, M: 28.5, L: 29, XL: 29.75, XXL: 30.5, XXXL: 31.25 },
      layer:     { S: 27.5, M: 28.125, L: 29, XL: 29.625, XXL: 30.5, XXXL: 31 },
      outerwear: { S: 27.5, M: 28.25, L: 29, XL: 30.25, XXL: 31.25, XXXL: 32.5 }
    },
    leg: { crotchRatio: 0.47, kneeTopRatio: 0.30, garmentDrop: 0.5, pantTargetHem: 1.75, ankleRatio: 0.045, joggerTargetAboveAnkle: -0.5, breakFull: 1.0, breakSlight: 2.5, breakNone: 4.0 },
    limits: { heightMin: 60, heightMax: 82, weightMin: 100, weightMax: 350, waistMin: 26, waistMax: 44 }
  };

  function median(a) { var s = a.slice().sort(function (x, y) { return x - y; }); var m = s.length >> 1; return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; }
  function round(n, p) { var f = Math.pow(10, p || 1); return Math.round(n * f) / f; }
  function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }

  /** Validate + normalise shopper input. Returns { ok, errors[], profile } */
  function normalizeProfile(input) {
    var L = CONFIG.limits, errors = [];
    var h = Number(input.heightIn), w = Number(input.weightLb), wa = Number(input.waist);
    if (!(h >= L.heightMin && h <= L.heightMax)) errors.push({ field: 'height', message: 'Enter a height between 5\'0" and 6\'10".' });
    if (!(w >= L.weightMin && w <= L.weightMax)) errors.push({ field: 'weight', message: 'Enter a weight between 100 and 350 lb.' });
    if (!(wa >= L.waistMin && wa <= L.waistMax)) errors.push({ field: 'waist', message: 'Enter a waist size between 26 and 44.' });
    return {
      ok: errors.length === 0, errors: errors,
      profile: { heightIn: h, weightLb: w, waist: wa,
        chestShape: CONFIG.chestShapeAdj[input.chestShape] !== undefined ? input.chestShape : 'average',
        stomachShape: CONFIG.stomachAdj[input.stomachShape] !== undefined ? input.stomachShape : 'average',
        thighShape: CONFIG.thighShapes.indexOf(input.thighShape) > -1 ? input.thighShape : 'average' }
    };
  }

  /** Estimate body measurements (inches). */
  function estimateBody(p) {
    var r = CONFIG.chestRegression;
    var bodyWaist = p.waist + CONFIG.labelToBodyWaist;
    var chest = r.base + r.perLb * (p.weightLb - r.weightRef) + r.perIn * (p.heightIn - r.heightRef)
      + CONFIG.stomachAdj[p.stomachShape] + CONFIG.chestShapeAdj[p.chestShape];
    var bmi = 703 * p.weightLb / (p.heightIn * p.heightIn);
    var hip = bodyWaist + CONFIG.hipFromWaist[p.stomachShape] + CONFIG.hipPerBmi * Math.max(0, bmi - CONFIG.hipBmiRef);
    return { chest: round(chest), waist: bodyWaist, hip: round(hip), heightIn: p.heightIn };
  }

  function sizesOf(spec) { return spec.sizes.filter(function (s) { return SIZE_ORDER.indexOf(s) > -1; }); }

  // Walk sorted targets, find the bracket the body value sits in.
  function locate(targets, value) {
    // targets: [{size, t}], ascending by t
    if (value <= targets[0].t) return { lo: 0, hi: 0, frac: 0, below: value < targets[0].t - (targets[1] ? (targets[1].t - targets[0].t) * 0.75 : 2) };
    var n = targets.length - 1;
    if (value >= targets[n].t) return { lo: n, hi: n, frac: 0, above: value > targets[n].t + (targets[n].t - targets[n - 1].t) * 0.75 };
    for (var i = 0; i < n; i++) {
      if (value >= targets[i].t && value <= targets[i + 1].t) return { lo: i, hi: i + 1, frac: (value - targets[i].t) / (targets[i + 1].t - targets[i].t) };
    }
  }

  function fitKey(spec) { return String(spec.fit || '').toLowerCase(); }

  function recommendTop(spec, body, p) {
    var sizes = sizesOf(spec), m = spec.measurements, chest = m.chest_circumference;
    var eases = sizes.map(function (s, i) { return chest[i] - CONFIG.chartChestMid[s]; });
    var ease = median(eases);
    // v1.5: merch override in body-chest inches. Positive sizes down (roomy cuts that run large), negative sizes up
    // (layering pieces that run small). Without it every top maps to the size chart regardless of how it's cut.
    var chestOffset = typeof spec.chest_offset_override === 'number' ? spec.chest_offset_override : 0;
    var targets = sizes.map(function (s, i) { return { size: s, i: i, t: chest[i] - ease + chestOffset }; });
    var loc = locate(targets, body.chest), notes = [], reason;
    var pick = loc.lo, between = false, alt = null;
    if (loc.hi !== loc.lo) {
      var band = CONFIG.topBetweenBand;
      if (loc.frac > band[1]) pick = loc.hi;
      else if (loc.frac >= band[0]) {
        between = true;
        var f = fitKey(spec), up = false;
        if ((f === 'athletic' || f === 'modern') && (p.chestShape === 'broad' || p.stomachShape === 'round')) up = true;
        else if (p.chestShape === 'broad' && p.stomachShape === 'round') up = true;
        else if (f === 'athletic' && loc.frac >= 0.5) up = true;
        pick = up ? loc.hi : loc.lo;
        alt = up ? loc.lo : loc.hi;
        reason = up ? 'sized_up_for_room' : 'sized_down_for_intended_fit';
      }
    }
    // Waist check for fitted tops (round stomach)
    var sweep = m.sweep ? m.sweep.map(function (x) { return spec.chest_basis === 'half' ? x * 2 : x; }) : null;
    if (sweep && sweep[pick] && sweep[pick] < body.waist + CONFIG.minSweepEase && pick < sizes.length - 1) {
      notes.push({ code: 'hem_snug', text: 'Hem may feel snug at your waist, so we moved you up a size.' });
      alt = pick; pick = pick + 1; between = true;
    }
    var lenNorm = CONFIG.bodyLengthNorm[spec.subtype];
    if (body.heightIn >= CONFIG.tallFrom && lenNorm && m.body_length && m.body_length[pick] && lenNorm[sizes[pick]] &&
        lenNorm[sizes[pick]] - m.body_length[pick] >= CONFIG.lengthShortBy) {
      notes.push({ code: 'length_short', text: 'This style runs a little shorter than our other ' + spec.subtype.replace('_', '-') + 's, so at your height it may fit short.' });
    }
    return finish(spec, sizes, pick, alt, between, loc, notes, reason, { bodyChest: body.chest, ease: round(ease), chestOffset: chestOffset });
  }

  function recommendBottom(spec, body, p) {
    var sizes = sizesOf(spec), m = spec.measurements;
    var offset = waistOffset(spec, sizes);
    var targets = sizes.map(function (s, i) { return { size: s, i: i, t: CONFIG.chartWaistTarget[s] + offset }; });
    var loc = locate(targets, body.waist), notes = [], reason;
    var pick = loc.lo, between = false, alt = null;
    if (loc.hi !== loc.lo) {
      var band = CONFIG.betweenBand;
      if (loc.frac > band[1]) pick = loc.hi;
      else if (loc.frac >= band[0]) {
        between = true;
        // v1.2: tie-break by build, not fit class. Very lean shoppers (BMI < 21) or a flat stomach lean smaller;
        // a round stomach or BMI ≥ 28 leans bigger; otherwise the nearer size.
        var bmi = 703 * p.weightLb / (p.heightIn * p.heightIn);
        var up;
        if (p.stomachShape === 'round' || bmi >= CONFIG.bmiLeanBig) up = true;
        else if (bmi < CONFIG.bmiLeanSmall || p.stomachShape === 'flat') up = false;
        else up = loc.frac >= 0.5;
        pick = up ? loc.hi : loc.lo; alt = up ? loc.lo : loc.hi;
        reason = up ? 'sized_up_for_room' : 'sized_down_for_intended_fit';
      }
      // v1.4: Modern fit runs slim through the thigh, so athletic thighs take the next size up.
      if (spec.type === 'bottom' && p.thighShape === 'athletic' && fitKey(spec) === 'modern' &&
          pick === loc.lo && loc.frac >= CONFIG.athleticModernUpFrom) {
        pick = loc.hi; alt = loc.lo; between = true; reason = 'sized_up_for_room';
        notes.push({ code: 'thigh_room', text: 'Modern fit runs slim through the thigh, so we moved you up a size.' });
      }
    }
    if (spec.type === 'bottom' && m.hip && m.hip[pick] && m.hip[pick] - body.hip < CONFIG.minHipEase && pick < sizes.length - 1) {
      notes.push({ code: 'seat_snug', text: 'Seat may feel snug, so we moved you up a size.' });
      alt = pick; pick = pick + 1; between = true;
    }
    var out = finish(spec, sizes, pick, alt, between, loc, notes, reason, { bodyWaist: body.waist, waistOffset: offset });
    if (spec.type === 'bottom') out.inseam = recommendInseam(spec, body.heightIn);
    return out;
  }

  /** How far this style's waistband runs from the catalog norm (inches, 0 if within threshold). Briefs: 0. */
  function waistOffset(spec, sizes) {
    var m = spec.measurements;
    if (typeof spec.waist_offset_override === 'number') return spec.waist_offset_override; // merch/PD override
    if (spec.type !== 'bottom' || !m.waist_relaxed || !m.waist_extended) return 0;
    var norm = CONFIG.waistbandNorm[(spec.subtype === 'pant' || spec.subtype === 'jogger') ? 'pants' : 'shorts'];
    var devs = sizes.map(function (s, i) { return (m.waist_relaxed[i] + m.waist_extended[i]) / 2 - norm[s]; });
    var dev = median(devs);
    return Math.abs(dev) >= CONFIG.waistOffsetThreshold ? dev : 0;
  }

  function finish(spec, sizes, pick, alt, between, loc, notes, reason, debug) {
    var confidence = 'high';
    if (between) confidence = 'medium';
    if (loc.below || loc.above) {
      confidence = 'low';
      notes.push({ code: loc.below ? 'below_range' : 'above_range',
        text: loc.below ? 'This style may run big on you. ' + sizes[0] + ' is our smallest size.'
                        : 'This style may fit snug. ' + sizes[sizes.length - 1] + ' is our largest size.' });
    }
    return { size: sizes[pick], alternate: alt !== null && alt !== pick ? sizes[alt] : null,
      between: between, confidence: confidence, reason: reason || null, notes: notes, debug: debug };
  }

  /** Leg geometry for the sketch + pant length pick. */
  function legGeometry(heightIn) {
    var g = CONFIG.leg;
    return { heightIn: heightIn, crotch: g.crotchRatio * heightIn, kneeTop: g.kneeTopRatio * heightIn, ankle: g.ankleRatio * heightIn };
  }

  function hemFor(heightIn, inseam) {
    var geo = legGeometry(heightIn), hem = geo.crotch - CONFIG.leg.garmentDrop - inseam;
    // hemHeight = inches above the floor; aboveAnkle = hem vs the ankle bone (negative = below it)
    return { hemHeight: round(hem), aboveKnee: round(hem - geo.kneeTop), aboveAnkle: round(hem - geo.ankle) };
  }

  function describeHem(inseam, h, isJogger) {
    if (inseam < 12) {
      var a = h.aboveKnee;
      var txt = a > 6 ? 'Shorter, above mid-thigh' : a > 4.5 ? 'Mid-thigh' : a > 2.5 ? 'Classic, just above the knee' : a > 0.5 ? 'Longer, just above the knee' : 'At or below the knee';
      return { label: txt, detail: null };  // no inches-above-knee detail (copy decision 9/24/2026)
    }
    if (isJogger) {
      // Joggers: the cuff holds the hem up, so describe where the cuff lands and how much fabric gathers above it.
      var d = h.aboveAnkle;
      if (d < -2) return { label: 'Extra relaxed', detail: 'Extra fabric gathers above the cuff for a more relaxed look' };
      if (d <= 1) return { label: 'Just right', detail: 'Cuff sits at your ankle with a little gather above it' };
      if (d <= 3) return { label: 'A little shorter', detail: 'Cuff sits just above your ankle for a cleaner look' };
      return { label: 'Short', detail: 'Cuff sits well above your ankle' };
    }
    var g = CONFIG.leg, b = h.hemHeight;
    // Plain language on purpose (not suiting terms like "break"). Internal bands: full / slight / no break / cropped.
    if (b < g.breakFull) return { label: 'Long', detail: 'Bunches up on top of your shoes' };
    if (b < g.breakSlight) return { label: 'Just right', detail: 'Lightly touches the top of your shoes' };
    if (b < g.breakNone) return { label: 'A little shorter', detail: 'Ends right at your ankle' };
    return { label: 'Short', detail: 'Shows some ankle' };
  }

  function recommendInseam(spec, heightIn) {
    var opts = (spec.inseams || []).map(function (o) {
      var h = hemFor(heightIn, o.actual);
      return { label: o.label, actual: o.actual, hem: h, description: describeHem(o.actual, h, spec.subtype === 'jogger') };
    });
    if (!opts.length) return null;
    var recommended = null;
    if (opts.length > 1 && opts[0].actual >= 12) {
      var jog = spec.subtype === 'jogger';
      var target = jog ? CONFIG.leg.joggerTargetAboveAnkle : CONFIG.leg.pantTargetHem;
      var val = function (o) { return jog ? o.hem.aboveAnkle : o.hem.hemHeight; };
      recommended = opts.reduce(function (best, o) { return Math.abs(val(o) - target) < Math.abs(val(best) - target) ? o : best; }).label;
    }
    // Shorts: no single "right" length, show both, recommendation left to preference.
    return { options: opts, recommended: recommended };
  }

  /** Main entry. spec = one record from fit_data.json. input = raw shopper answers. */
  function recommend(spec, input) {
    var n = normalizeProfile(input);
    if (!n.ok) return { ok: false, errors: n.errors };
    // v1.4: bottoms and briefs don't ask about stomach, so a stomach answer saved from a top is ignored here.
    if (spec.type !== 'top') n.profile.stomachShape = 'average';
    var body = estimateBody(n.profile);
    var r;
    if (spec.type === 'top') {
      if (!spec.measurements.chest_circumference) return { ok: false, errors: [{ field: 'spec', message: 'No chest measurement for this style.' }] };
      r = recommendTop(spec, body, n.profile);
    } else {
      if (spec.type !== 'brief' && !spec.measurements.waist_relaxed) return { ok: false, errors: [{ field: 'spec', message: 'No waist measurement for this style.' }] };
      r = recommendBottom(spec, body, n.profile);
    }
    r.ok = true; r.body = body; r.style = spec.style_name; r.fit = spec.fit;
    return r;
  }

  return { recommend: recommend, waistOffset: waistOffset, estimateBody: estimateBody, normalizeProfile: normalizeProfile,
    legGeometry: legGeometry, hemFor: hemFor, recommendInseam: recommendInseam, CONFIG: CONFIG, SIZE_ORDER: SIZE_ORDER };
});
